import { ErrorMessage, Field, Form, Formik } from "formik";
import { useEffect, useMemo, useRef, useState } from "react";
import * as Yup from "yup";
import IconImageDefault from "../../../assets/icons/ic_image_default.svg";
import { ButtonCustom } from "../../../components/custom/button-custom";
import { showToast } from "../../../components/custom/custom-toast";
import ImageCustom from "../../../components/custom/image-custom";
import IconCamera from "../../../components/icons/icon-camera";
import { useFetchData } from "../../../hooks/useFetch";
import useUpload from "../../../hooks/useUpload";
import { Banner } from "../../../models/response/banner";
import { API_BANNER_ROUTER, API_UPLOAD_ROUTER } from "../../../services/api-config";

interface formValuesBanner {
  name: string;
  link: string;
  image: string;
  imageUploads: FileList | null;
  imageName: string;
}

interface BannerActionsModalProps {
  dataBanners: Banner[];
}

export const BannerActionsModal = (props: BannerActionsModalProps) => {
  const lastItemRef = useRef<HTMLDivElement>(null);
  const [imageUploads, setImageUploads] = useState<FileList | null>(null);
  const [imageDisplays, setImageDisplays] = useState<string[]>([]);
  const [isImageLocals, setIsImageLocals] = useState<boolean[]>([]);

  const initialValues = useMemo(
    () => ({
      banners: [
        {
          name: "",
          link: "",
          image: "",
          imageUploads: null,
          imageName: "",
        },
        {
          name: "",
          link: "",
          image: "",
          imageUploads: null,
          imageName: "",
        },
      ],
    }),
    [props.dataBanners]
  );

  const [formValues, setFormValues] = useState<{
    banners: formValuesBanner[];
  }>(initialValues);

  const { uploadFiles: onUploadFiles, baseResponse: responseUpload } = useUpload<any>(API_UPLOAD_ROUTER.API_UPLOAD);
  const { request: createApi, baseResponse: createRes } = useFetchData.Post<Banner>();

  useEffect(() => {
    if (props.dataBanners.length === 0) {
      setFormValues({
        banners: [
          {
            name: "",
            link: "",
            image: "",
            imageUploads: null,
            imageName: "",
          },
          {
            name: "",
            link: "",
            image: "",
            imageUploads: null,
            imageName: "",
          },
        ],
      });
    } else {
      setFormValues({
        banners: props.dataBanners.map((item) => ({
          name: item.name,
          link: item.link,
          image: item.image,
          imageUploads: null,
          imageName: "",
        })),
      });
    }

    setImageDisplays(props.dataBanners.map((item) => item.image));
    setIsImageLocals(props.dataBanners.map((item) => item.image === ""));
  }, [props.dataBanners, initialValues]);

  const validationSchema = Yup.object().shape({
    banners: Yup.array().of(
      Yup.object().shape({
        name: Yup.string()
          .trim()
          .min(2, "Độ dài tối thiểu 2 kí tự")
          .max(255, "Tối đa 255 ký tự")
          .required("Trường dữ liệu bắt buộc"),
        link: Yup.string().trim().min(2, "Độ dài tối thiểu 2 kí tự"),
        image: Yup.string().required("Trường dữ liệu bắt buộc"),
      })
    ),
  });

  const createBanners = async (data: any) => {
    console.log("data", data);
    const body = {
      banners: data.banners.map((banner: any) => ({
        name: banner.name,
        image: banner.image,
        link: banner.link,
      })),
    };

    await createApi({
      endPoint: API_BANNER_ROUTER.API_CREATE_UPSERT(),
      data: body,
    });
  };

  useEffect(() => {
    if (!createRes) return;
    if (createRes?.status === 200) {
      showToast({ message: "Cập nhật thành công", type: "success" });
    } else {
      showToast({ message: createRes?.message ?? "Cập nhật thất bại", type: "error" });
    }
  }, [createRes]);

  const getImageUploads = (): FileList => {
    const images: File[] = [];

    formValues.banners.forEach((banner) => {
      if (banner.imageUploads) {
        Array.from(banner.imageUploads).forEach((file) => {
          images.push(file);
        });
      }
    });

    const dataTransfer = new DataTransfer();
    images.forEach((file) => dataTransfer.items.add(file));

    return dataTransfer.files;
  };

  const handleSubmit = (values: any) => {
    if (imageUploads) {
      onUploadFiles(getImageUploads());
    } else {
      createBanners(values);
    }
  };

  const handleImageChange = (index: number, event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files;
    if (file && file.length > 0) {
      const updatedImageDisplays = [...imageDisplays];
      const updatedIsImageLocals = [...isImageLocals];
      updatedIsImageLocals[index] = true;

      setIsImageLocals(updatedIsImageLocals);

      const reader = new FileReader();
      reader.onloadend = () => {
        updatedImageDisplays[index] = reader.result as string;
        setImageDisplays(updatedImageDisplays);
      };
      reader.readAsDataURL(file[0]);
      setImageUploads(file);
      const updatedFormValues = { ...formValues };
      updatedFormValues.banners[index].image = file[0].name;
      updatedFormValues.banners[index].imageUploads = file;
      updatedFormValues.banners[index].imageName = file[0].name;

      setFormValues(updatedFormValues);
    }
  };

  const mapImageResponse = (paths: string[]): { banners: formValuesBanner[] } => {
    let pathIndex = 0; // Chỉ số để lấy ảnh từ paths

    const updatedFormValues: formValuesBanner[] = formValues.banners.map((banner) => {
      if (banner.imageUploads && pathIndex < paths.length) {
        const updatedBanner = {
          ...banner,
          image: paths[pathIndex], // Gán ảnh mới từ paths
        };
        pathIndex++; // Chỉ tăng khi đã gán ảnh
        return updatedBanner;
      }
      return banner; // Giữ nguyên nếu không có ảnh mới
    });

    const result = { banners: updatedFormValues };
    setFormValues(result);
    return result;
  };

  useEffect(() => {
    if (!responseUpload) return;
    if (responseUpload.status === 200 && responseUpload.data.length > 0) {
      const updatedFormValues = mapImageResponse(responseUpload.data);

      createBanners(updatedFormValues);
    } else {
      showToast({ message: "Có lỗi xảy ra vui lòng thử lại sau", type: "warning" });
    }
  }, [responseUpload]);

  return (
    <Formik
      enableReinitialize={true}
      initialValues={formValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ values, isSubmitting, setFieldValue, submitForm }) => {
        const bodyModal = () => (
          <Form>
            <div className="flex items-center justify-center  gap-2  ">
              {values.banners.map((banner, index) => {
                return (
                  <div
                    key={index}
                    className="flex gap-2 flex-col bg-white rounded-lg shadow p-4 "
                    ref={index === formValues.banners.length - 1 ? lastItemRef : null}
                  >
                    <div className="font-semibold">Banner phía {`${index === 0 ? "trên" : "dưới"}:`}</div>
                    <div className="h-[24rem] w-full">
                      <div className="relative group  flex items-center justify-center">
                        <ImageCustom
                          url={imageDisplays[index] || IconImageDefault}
                          className={`"rounded  object-scale-down ${
                            index == 0 ? "h-[10rem]" : "h-[20rem]"
                          } w-[31.25rem]  "`}
                          isLocal={isImageLocals[index]}
                          fallBackUrl={IconImageDefault}
                          // isReview={true}
                        />
                        <div className="absolute inset-0 cursor-pointer">
                          <input
                            id={`input-file-${index}`}
                            type="file"
                            name={`banners[${index}].image`}
                            accept="image/*"
                            className="absolute inset-0 opacity-0 cursor-pointer"
                            onChange={(e) => handleImageChange(index, e)}
                          />
                        </div>

                        <div className="flex items-center gap-2 absolute top-4 right-4  scale-0 group-hover:scale-100 transition-transform duration-300">
                          <label
                            htmlFor={`input-file-${index}`}
                            className="cursor-pointer p-2 border border-white rounded-full bg-black/50"
                          >
                            <IconCamera fill color="white" className="cursor-pointer" />
                          </label>
                        </div>
                      </div>

                      <ErrorMessage
                        name={`banners[${index}].image`}
                        component="div"
                        className="text-red-500 text-xs mt-2 text-center"
                      />
                    </div>

                    <div className="mt-3">
                      <label htmlFor={`banners[${index}].name`} className="block font-medium text-sm mb-1">
                        Tên banner <span className="text-red-500">*</span>
                      </label>
                      <Field
                        autoComplete="off"
                        value={banner.name}
                        name={`banners[${index}].name`}
                        type="text"
                        id={`banners[${index}].name`}
                        placeholder="Nhập tên banner"
                        className="form-input w-full"
                        maxLength={255}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          const newValue = e.target.value;
                          setFieldValue(`banners[${index}].name`, newValue);
                          setFormValues((prev) => ({
                            ...prev,
                            banners: prev.banners.map((b, i) => (i === index ? { ...b, name: newValue } : b)),
                          }));
                        }}
                      />
                      <ErrorMessage
                        name={`banners[${index}].name`}
                        component="div"
                        className="mt-2 text-xs text-red-500"
                      />
                    </div>

                    <div className="">
                      <label htmlFor={`banners[${index}].link`} className="block font-medium text-sm mb-1">
                        Link banner <span className="text-red-500">*</span>
                      </label>
                      <Field>
                        {({ field }: any) => (
                          <textarea
                            {...field}
                            rows={3}
                            className="form-textarea"
                            placeholder="Nhập thông tin"
                            required
                            maxLength={255}
                            value={banner.link}
                            name={`banners[${index}].link`}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                              const newValue = e.target.value;
                              setFieldValue(`banners[${index}].link`, newValue);
                              setFormValues((prev) => ({
                                ...prev,
                                banners: prev.banners.map((b, i) => (i === index ? { ...b, link: newValue } : b)),
                              }));
                            }}
                          />
                        )}
                      </Field>
                      <ErrorMessage
                        name={`banners[${index}].link`}
                        component="div"
                        className="mt-2 text-xs text-red-500"
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </Form>
        );

        return (
          <>
            {bodyModal()}
            <div className="flex justify-center gap-2">
              <ButtonCustom disabled={isSubmitting} type="button" text="Cập nhật" onClick={submitForm} />
            </div>
          </>
        );
      }}
    </Formik>
  );
};
